import { useState } from "react";
import PageHeader from "@/components/molecules/PageHeader";
import Container from "@/components/atoms/Container";
import Button from "@/components/atoms/Button";
import { Undo2 } from "lucide-react";
import Link from "@/components/atoms/Link";
import { router } from "@inertiajs/react";
import { useUserStore } from "@/store";
import Spinner from '@/components/atoms/Spinner';

type Props = {
  tickets: any;
};

const Index = ({ tickets }: Props) => {
  const currentUser = useUserStore((state) => state.currentUser);
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const breadcrumbItems = [
    {
      label: "User info",
      onClick: () => router.get("/profile"),
    },
  ];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxSizeInBytes = 1 * 1024 * 1024;
    const allowedImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size > maxSizeInBytes) {
        alert('File size exceeds the 1MB limit. Please select a smaller file.');
        event.target.value = '';
      } else if(!allowedImageTypes.includes(event.target.files[0].type)) {
        alert("Only image files (JPEG, PNG, GIF, WebP) are allowed. Please select a valid image.");
        event.target.value = '';
      } else {
        setAvatarFile(event.target.files[0]);
      }
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      alert("Please select an avatar file to upload.");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("user_avatar", avatarFile);

    try {
      const response = await fetch("/admin/users/update_avatar", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
      });

      const data = await response.json();

      if (response.ok) {
        setLoading(false);
        
        const currentUser = useUserStore.getState().currentUser;
        if (currentUser) {
          useUserStore.getState().setCurrentUser({
            ...currentUser,
            avatar_url: data.avatar_urls.avatar_url,
            avatar_url_medium: data.avatar_urls.avatar_url_medium,
          });
        }
      } else {
        alert("Failed to update avatar.");
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <Spinner />
        </div>
      )}

      <Container>
        <PageHeader title="User info" breadcrumbItems={breadcrumbItems}>
          <div className="space-x-2">
          <Link href="/">
            <Button className="inline-flex items-center gap-2 text-black">
              <Undo2 size={16} /> <span>Back</span>
            </Button>
            </Link>
          </div>
        </PageHeader>
      </Container>

      <Container>
        <div className="pb-4 pt-2">
          <div className="flex items-center p-4 space-x-8 shadow-md border border-border rounded-md">
            <div>
              <img
                src={currentUser && "avatar_url" in currentUser ? currentUser.avatar_url_medium : ""}
                alt="Profile"
                className="w-24 h-24 rounded-full dark:bg-darkTextColor"
              />
            </div>
            <div className="flex items-center justify-between w-9/12">

              <div className="flex flex-col">
                <div className="flex items-center space-x-1 text-gray-800">
                  <span className="font-semibold text-lg dark:text-darkTextColor">{currentUser?.name}</span>
                  <span className="text-sm text-gray-500 dark:text-darkTextColor">({currentUser?.roles.map(role => role.name).join(", ")})</span>
                </div>
                <div className="text-gray-600 dark:text-darkTextColor">{currentUser?.title}</div>
              </div>
              <div className="flex flex-col text-right items-start ">
                <div className="text-gray-600 dark:text-darkTextColor">{currentUser?.email}</div>
                <div className="text-gray-500 dark:text-darkTextColor">{currentUser?.status}</div>
                {currentUser?.supervisor_name && <div className="text-gray-500 dark:text-darkTextColor">Reports to {currentUser?.supervisor_name.split(", ").reverse().join(" ")}</div>}
              </div>
            </div>
          </div>

        </div>
        <div className="pb-4 pt-2">
          <div className="flex items-center p-4 space-x-8 shadow-md border border-border rounded-md">
            <div className="flex flex-col space-y-2">
              <label htmlFor="avatar" className="font-medium">
                Upload New Avatar
              </label>
              <input
                type="file"
                id="avatar"
                accept="image/*"
                className="file:cursor-pointer file:text-sm file:font-medium dark:file:text-darkTextColor disabled:file:cursor-not-allowed"
                onChange={handleFileChange}
              />
              <Button
                onClick={handleAvatarUpload}
                className="text-black rounded-md !mt-4 w-44"
                disabled={loading}
              >
                {loading ? 'Avatar Updating..' : 'Update Avatar'}
              </Button>
            </div>
          </div>
        </div>
  
      </Container>
    </>
  );
};

export default Index;
